import {
	connectToRouter,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import {
	Guid,
	newGuid,
	tryParseGuid
} from "client_legacy/ndaw/guid";
import {
	createTryParseNumericEnum,
	tryParseInt
} from "client/Misc/parseUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import { integrationBuilderRouting } from "./OverViewPage";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import {
	Form,
	FormItem,
	Select,
	SelectItem,
	SelectReadOnly,
	Tabs,
	TextInputReadOnly,
	TextInput,
	ToolbarAction,
	ValidationError,
	AlertBar,
	FormattedText,
	Checkbox,
	CheckboxReadOnly,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { IValueInputAttachable } from "client/Forms/attachable";
import classNames from "classnames";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import styles from "./EditIntegrationFlowPage.module.less";
import {
	AppIconSelector,
	attachToAppIconSelector,
	convertColorToViewModel
} from "client/Features/Apps/AppIconSelector";
import {
	AppColorKind,
	AppColorViewModel
} from "client/Api/Apps/common";
import { AppIconModel } from "client_legacy/AppIcon/common";
import { AppColorSelectFormItem } from "client/Features/Apps/AppColorSelectFormItem";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { attachToUserAndUserGroupCollection } from "client/Features/Users/UserAndUserGroupCollection/attachable";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { StepList } from "./StepList";
import {
	SettingItem,
	SettingsTab
} from "./SettingsTab";
import {
	useSettingsDialog
} from "./SettingsDialog";
import {
	getClientId,
	ObjectWithClientId,
	setClientId,
	setExistingClientId
} from "client/Misc/clientId";
import {
	createFlow,
	CreateIntegrationFlowResultModel,
	getEditViewModel,
	getNewViewModel,
	IntegrationFlowAuthenticationOptionModel,
	IntegrationFlowConfigurationPageViewModel,
	IntegrationFlowConfigurationSaveModel,
	IntegrationFlowConfigurationViewModel,
	IntegrationFlowKind,
	IntegrationFlowPermissionsSaveModel,
	IntegrationFlowSettingSaveModel,
	IntegrationFlowSettingViewModel,
	IntegrationSettingScopeKind,
	MappingStepConfigurationSaveModel,
	StepConfigurationSaveModel,
	StepConfigurationViewModel,
	StepKind,
	MappingStepConfigurationViewModel,
	IntegrationFlowConfigurationValidateModel,
	IntegrationFlowConfigurationValidateResponseModel,
	validate,
	IntegrationFlowRequestResultModel,
	editFlow,
	IntegrationFlowStatus,
	RequestMethod,
	GeneralMappingStepConfigurationViewModel,
	ResultMapperStyledFieldSaveModel,
	ResultMapperStyledFieldViewModel,
	IntegrationFlowCapabilityKind,
	IntegrationFlowInputDataModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IntegrationSharedSettingOptionModel } from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { addSearchIntegrationFlow } from "client/Api/SearchSettings/searchSettings";
import { useStepKindSelectionDialog } from "./StepKindSelectionDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { never } from "client_legacy/ndaw/errors";
import { union } from "client_legacy/ndaw/arrayHelpers";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { integrationFlowRequiredUserLicense } from "./ManageIntegrationPermissionsDialog";
import {
	StepDialog,
	StepDialogInitialData
} from "./StepDialog";
import {
	createEmptyInputData,
	getResultMapperStepStatus,
	globalSearchCapabilities,
	InputDataFormData,
	integrationFlowKindToCapabilities,
	integrationFlowKindToLocalizationKey,
	isGeneralValidationResults,
	MappingStepConfiguration,
	matchesCapabilitiesFilter,
	PerItemRequestStepConfiguration,
	RequestStepConfiguration,
	RequestStepKind,
	requiresInputData,
	StepConfiguration,
	tryParseErrorResult
} from "./common";
import {
	ResultMapperDialog
} from "./ResultMapperDialog";
import { useConfigureInputDataDialog } from "./ConfigureInputDataDialog";
import { ClientValidationErrorModel } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotEmpty
} from "client/Validation/rules";
import { useUnsavedChangesDialog } from "./UnsavedChangesDialog";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { useIntegrationFlowAuthenticationRequiredDialog } from "./IntegrationFlowAuthenticationRequiredDialog";
import { IntegrationFlowTagSelect } from "./IntegrationFlowTagSelect";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";

export type EditIntegrationFlowPageProps = {
	appId: number;
	onNavigate: NavigationCallback;
} & (
		{
			integrationId?: undefined;
			versionId?: undefined;
			flowKind: IntegrationFlowKind;
			capabilities: readonly IntegrationFlowCapabilityKind[];
		}
		|
		{
			integrationId: number;
			versionId: Guid;
			flowKind?: undefined;
			capabilities?: undefined;
		}
	);

export const EditIntegrationFlowPage = (props: EditIntegrationFlowPageProps): ReactElement => {
	const {
		appId,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>({
		users: EMPTY_ARRAY,
		userGroups: EMPTY_ARRAY
	});

	const [initialFormData, setInitialFormData] = useState<FormData>(defaultFormData);
	const [formData, setFormData] = useState<FormData>(defaultFormData);
	const [stepResults, setStepResults] = useState<Map<Guid, IntegrationFlowRequestResultModel>>(() => new Map<Guid, IntegrationFlowRequestResultModel>());
	const [authentications, setAuthentications] = useState<readonly IntegrationFlowAuthenticationOptionModel[]>(EMPTY_ARRAY);
	const [sharedSettings, setSharedSettings] = useState<readonly IntegrationSharedSettingOptionModel[]>(EMPTY_ARRAY);
	const [serverErrors, setServerErrors] = useState<readonly ClientValidationErrorModel[]>(EMPTY_ARRAY);
	const [validateResponse, setValidateResponse] = useState<IntegrationFlowConfigurationValidateResponseModel | null>(null);
	const [requestStepDialogState, setRequestStepDialogState] = useState<RequestStepDialogState | null>(null);
	const [resultMapperDialogState, setResultMapperDialogState] = useState<ResultMapperDialogState | null>(null);
	const [hasLiveVersion, setHasLiveVersion] = useState<boolean>(false);
	const [isAllowedToConfigureSearchSettings, setIsAllowedToConfigureSearchSettings] = useState<boolean>(false);
	const [selectedSettings, setSelectedSettings] = useState<readonly SettingItem[]>(EMPTY_ARRAY);
	const [activeTabId, setActiveTabId] = useState<TabId>("general");

	const [configureInputDataDialog, openConfigureInputDataDialog] = useConfigureInputDataDialog();
	const [unsavedChangesDialog, openUnsavedChangesDialog] = useUnsavedChangesDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [integrationFlowAuthenticationRequiredDialog, openIntegrationFlowAuthenticationRequiredDialog] = useIntegrationFlowAuthenticationRequiredDialog();
	const [stepKindSelectionDialog, openStepKindSelectionDialog] = useStepKindSelectionDialog();
	const [settingsDialog, openSettingsDialog] = useSettingsDialog(
		sharedSettings,
		(sharedSetting: IntegrationSharedSettingOptionModel): void => {
			setSharedSettings(
				sharedSettings => ([
					...sharedSettings,
					sharedSetting
				])
			);
		}
	);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const inputDataRef: RefObject<InputDataFormData | null> = useRef(null);
	const [notifyAboutNewInputData, inputDataVersion] = useForceRerender();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const requestSteps = useMemo(
		(): readonly ObjectWithClientId<RequestStepConfiguration | PerItemRequestStepConfiguration>[] => formData
			.steps
			.filter(
				x => x.kind === StepKind.Request || x.kind === StepKind.PerItemRequest
			),
		[formData.steps]
	);

	const stepWithErrorIds = useMemo(
		(): ReadonlySet<Guid> => getStepWithErrorsIds(formData.steps, serverErrors),
		[
			serverErrors,
			formData.steps
		]
	);

	const hasInputData = useMemo(
		(): boolean => {
			hasImplicitDependencyOn(inputDataVersion);

			return isNotNil(inputDataRef.current);
		},
		[
			inputDataVersion,
			inputDataRef
		]
	);

	const compositeId = useMemo(
		(): CompositeId => {
			if (isNotNil(props.integrationId)) {
				return {
					kind: "edit",
					integrationId: props.integrationId,
					versionId: ensureNotNil(props.versionId)
				};
			}

			return {
				kind: "new",
				flowKind: props.flowKind ?? IntegrationFlowKind.General,
				capabilities: props.capabilities ?? EMPTY_ARRAY
			};
		},
		[
			props.flowKind,
			props.capabilities,
			props.integrationId,
			props.versionId
		]
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useActiveSettingsMenuItem(SettingsPageKind.IntegrationBuilder);

	const canPublish = useMemo(
		(): boolean => {
			const hasMinimumSteps: boolean = formData
				.steps
				.length >= MIN_STEP_COUNT;

			const mappingStep: ObjectWithClientId<MappingStepConfiguration> | undefined = formData
				.steps
				.find((x) => x.kind === StepKind.Mapping);

			if (!mappingStep || !hasMinimumSteps) {
				return false;
			}

			return isNotNil(mappingStep.mapping)
				&& formHandler
					.validation
					.isValid();
		},
		[
			formData.steps,
			formHandler.validation
		]
	);

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	const getInputData = useCallback(
		async (): Promise<TryResult<InputDataFormData>> => {
			if (isNotNil(inputDataRef.current)) {
				return {
					wasSuccessful: true,
					result: inputDataRef.current
				};
			}

			if (!requiresInputData(formData.capabilities)) {
				throw new Error(`The integration capabilities ${formData.capabilities.join(", ")} do not need input data`);
			}

			const inputDataResult: TryResult<InputDataFormData> = await openConfigureInputDataDialog({
				initialData: createEmptyInputData(formData.capabilities)
			});

			if (!inputDataResult.wasSuccessful) {
				return { wasSuccessful: false };
			}

			inputDataRef.current = inputDataResult.result;
			notifyAboutNewInputData();

			return {
				wasSuccessful: true,
				result: inputDataResult.result
			};
		},
		[
			formData.capabilities,
			openConfigureInputDataDialog,
			notifyAboutNewInputData
		]
	);

	const backToAppHandler = useCallback(
		(): void => {
			onNavigate(
				integrationBuilderRouting.editApp({
					routeParams: {
						appId: `${appId}`
					},
					queryParams: {
						tab: "flows"
					}
				})
			);
		},
		[appId, onNavigate]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setServerErrors([]);
			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				const isEdit = compositeId.kind === "edit";
				const isLive: boolean = false;

				const model: IntegrationFlowConfigurationSaveModel = prepareForSaving(
					formData,
					inputDataRef.current,
					isEdit
				);

				if (isEdit) {
					const {
						integrationId,
						versionId
					} = compositeId;

					await editFlow(
						integrationId,
						versionId,
						model,
						isLive,
						globalCancellation
					);
				} else {
					await createFlow(
						appId,
						model,
						isLive,
						globalCancellation
					);
				}

				alertManager.showSuccess(localize("_SuccessfullySavedTheFlow"));
				setIsBusy(false);
				backToAppHandler();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				const result: TryResult<readonly ClientValidationErrorModel[]> = tryGetServerErrors(e);

				if (!result.wasSuccessful) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
					return;
				}

				const {
					result: errors
				} = result;

				formHandler
					.validation
					.setServerErrors(convertServerErrors(errors));

				if (hasInputDataError(formData, errors)
					|| hasStepErrors(errors)
				) {
					alertManager.showError(localize("_ErrorWhileValidatingTheSteps"));
				}

				setServerErrors(result.result);
				setIsBusy(false);
			}
		},
		[
			alertManager,
			appId,
			backToAppHandler,
			formHandler.validation,
			globalCancellation,
			isBusy,
			formData,
			compositeId
		]
	);

	const backHandler = useCallback(
		async(): Promise<void> => {
			if (isDataChanged) {
				const result: TryResult<boolean> = await openUnsavedChangesDialog({});

				if (!result.wasSuccessful) {
					return;
				}

				if (result.result) {
					saveHandler();
				} else {
					backToAppHandler();
				}
			} else {
				backToAppHandler();
			}
		},
		[
			backToAppHandler,
			openUnsavedChangesDialog,
			isDataChanged,
			saveHandler
		]
	);

	const publishHandler = useCallback(
		async(): Promise<void> => {
			if (isBusy) {
				return;
			}

			setServerErrors([]);
			setIsBusy(true);

			try {
				let resolvedInputData: InputDataFormData | null = null;

				if (requiresInputData(formData.capabilities)) {
					const result: TryResult<InputDataFormData> = await getInputData();

					if (!result.wasSuccessful) {
						setIsBusy(false);
						return;
					}

					resolvedInputData = result.result;
				}

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				const confirmResult: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToPublishThisVersion"),
					children: hasLiveVersion
						? localize("_NoteThisVersionWillOverwriteTheExistingLiveVersion")
						: undefined,
					confirmText: localize("_Publish"),
					trackingId: "publish-version-confirmation-dialog"
				});

				if (!confirmResult.wasSuccessful) {
					setIsBusy(false);
					return;
				}

				const isLive: boolean = true;
				const isEdit = compositeId.kind === "edit";

				const model: IntegrationFlowConfigurationSaveModel = prepareForSaving(
					formData,
					resolvedInputData,
					isEdit
				);

				let integrationId: number;

				if (isEdit) {
					const {
						integrationId: editedIntegrationId,
						versionId
					} = compositeId;

					await editFlow(
						editedIntegrationId,
						versionId,
						model,
						isLive,
						globalCancellation
					);

					integrationId = editedIntegrationId;
				} else {
					const createResult: CreateIntegrationFlowResultModel = await createFlow(
						appId,
						model,
						isLive,
						globalCancellation
					);

					integrationId = createResult.integrationId;
				}

				alertManager.showSuccess(localize("_SuccessfullyPublishedTheFlow"));

				if (
					!hasLiveVersion
						&& isAllowedToConfigureSearchSettings
						&& matchesCapabilitiesFilter(formData.capabilities, globalSearchCapabilities)
				) {
					const addToGlobalSearchResult: TryResult<void> = await openConfirmationDialog({
						title: localize("_AddThisIntegrationToGlobalSearch"),
						children: localize("_ThisWillMakeTheItemsSearchableFromTheGlobalSearchBar"),
						confirmText: localize("_Add"),
						cancelText: localize("_DontAdd"),
						trackingId: "add-to-global-search-confirmation-dialog"
					});

					if (addToGlobalSearchResult.wasSuccessful) {
						try {
							await addSearchIntegrationFlow(
								{ integrationId },
								globalCancellation
							);
						} catch (e) {
							if (isCancelledError(e)) {
								return;
							}

							console.error(e);
							alertManager.showError(
								localize("_TheFlowWasPublishedButCouldNotBeAddedToGlobalSearchYouCanAddItFromTheSearchSettings")
							);
						}
					}
				}

				setIsBusy(false);
				backToAppHandler();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				const result: TryResult<readonly ClientValidationErrorModel[]> = tryGetServerErrors(e);
				let isHandled: boolean = false;

				if (result.wasSuccessful) {
					const {
						result: errors
					} = result;

					formHandler
						.validation
						.setServerErrors(convertServerErrors(errors));

					if (hasInputDataError(formData, errors)
						|| hasStepErrors(errors)
					) {
						alertManager.showError(localize("_ErrorWhileValidatingTheSteps"));
					}

					setServerErrors(errors);
					isHandled = true;
				} else {
					const authenticationRequiredResult: TryResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel> = tryParseErrorResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel>(e);

					if (authenticationRequiredResult.wasSuccessful) {
						isHandled = true;

						const {
							authentications: missingAuthentications
						} = authenticationRequiredResult.result;

						const result: TryResult<void> = await openIntegrationFlowAuthenticationRequiredDialog({
							integrationName: !formData.name
								? undefined
								: formData.name,
							missingAuthentications
						});

						if (result.wasSuccessful) {
							publishHandler();
							return;
						}
					}
				}

				if (!isHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			appId,
			backToAppHandler,
			formData,
			formHandler.validation,
			getInputData,
			globalCancellation,
			isBusy,
			compositeId,
			hasLiveVersion,
			isAllowedToConfigureSearchSettings,
			openConfirmationDialog,
			openIntegrationFlowAuthenticationRequiredDialog
		]
	);

	const clearAllServerErrorsHandler = useCallback(
		(): void => {
			formHandler
				.validation
				.clearServerErrors();

			setServerErrors([]);
		},
		[formHandler.validation]
	);

	const openSettingsDialogHandler = useCallback(
		async (item?: IntegrationFlowSettingSaveModel): Promise<void> => {
			const settings: readonly IntegrationFlowSettingSaveModel[] = formData.settings;
			const settingsAttachable: IValueInputAttachable<
				readonly IntegrationFlowSettingSaveModel[]
			> = formHandler.for("settings");

			const itemIndex: number = settings.findIndex(x => x.key === item?.key);

			const isEdit: boolean = itemIndex !== -1;

			const settingsWithoutCurrentOne = settings.filter(
				x => x.key !== item?.key
			);

			const result: TryResult<IntegrationFlowSettingSaveModel> = await openSettingsDialog({
				appId,
				initialData: item,
				existingKeys: settingsWithoutCurrentOne.map(x => x.key),
				existingNames: settingsWithoutCurrentOne
					.filter(x => x.scopeKind === IntegrationSettingScopeKind.ThisFlow)
					.map(x => x.displayName),
				serverErrors: isEdit
					? getSettingServerErrors(serverErrors, itemIndex)
					: EMPTY_ARRAY,
				isReadOnly
			});

			if (!result.wasSuccessful) {
				return;
			}

			if (!isEdit) {
				settingsAttachable.reducer
					.push(result.result);
			} else {
				settingsAttachable.forIndex(itemIndex)
					.reducer
					.setValue(result.result);

				clearAllServerErrorsHandler();
			}

			settingsAttachable.validation
				.triggerInputFinished();

			setSelectedSettings(EMPTY_ARRAY);
		},
		[
			appId,
			clearAllServerErrorsHandler,
			formData.settings,
			formHandler,
			isReadOnly,
			openSettingsDialog,
			serverErrors
		]
	);

	const deleteSelectedSettingsHandler = useCallback(
		async (): Promise<void> => {
			if (selectedSettings.length === 0) {
				return;
			}

			const title: string = selectedSettings.length > 1
				? localize("_DeleteThisItems").replace(
					"{itemCount}",
					selectedSettings.length
						.toString()
				)
				: localize("_DeleteThisItem");

			const result: TryResult<void> = await openConfirmationDialog({
				title,
				confirmText: localize("_Delete"),
				trackingId: "delete-setting-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			const settingsAttachable: IValueInputAttachable<readonly IntegrationFlowSettingSaveModel[]> =
				formHandler.for("settings");

			settingsAttachable.reducer
				.remove(
					setting => selectedSettings.some(
						x => x.key === setting.key
					)
				);

			settingsAttachable.validation
				.triggerInputFinished();

			clearAllServerErrorsHandler();

			setSelectedSettings(EMPTY_ARRAY);
		},
		[
			clearAllServerErrorsHandler,
			formHandler,
			openConfirmationDialog,
			selectedSettings
		]
	);

	const defaultActionHandlers = useMemo(
		(): DefaultActionHandlers => ({
			save: saveHandler,
			publish: publishHandler,
			cancel: backHandler
		}),
		[
			saveHandler,
			publishHandler,
			backHandler
		]
	);

	const contextualActionHandlers = useMemo(
		(): ContextualActionHandlers => ({
			onSettingEdit: openSettingsDialogHandler,
			onDeleteSelected: deleteSelectedSettingsHandler
		}),
		[openSettingsDialogHandler, deleteSelectedSettingsHandler]
	);

	const selectionActions = useMemo(
		(): readonly FlowAction[] => getSelectionActions({
			deselectHandler: () => setSelectedSettings(EMPTY_ARRAY)
		}),
		[]
	);

	const contextualActions = useMemo(
		(): readonly FlowAction[] => listContextualActions(contextualActionHandlers),
		[contextualActionHandlers]
	);

	const defaultActions = useMemo(
		(): readonly FlowAction[] => {
			if (isReadOnly) {
				return [];
			}

			return listDefaultActions(defaultActionHandlers);
		},
		[
			defaultActionHandlers,
			isReadOnly
		]
	);

	const hasSettingsSelection: boolean = activeTabId === "settings"
		&& selectedSettings.length > 0;

	const settingIndicesWithServerErrors: ReadonlySet<number> = useMemo(
		(): ReadonlySet<number> => {
			const indices: Set<number> = new Set<number>();

			for (const error of serverErrors) {
				const indexResult: TryResult<number> = tryExtractSettingsIndexFromPropertyPath(
					error.propertyName
				);

				if (indexResult.wasSuccessful) {
					indices.add(indexResult.result);
				}
			}

			return indices;
		},
		[serverErrors]
	);

	const topBarActions = useMemo(
		(): readonly FlowAction[] => {
			return buildActionsForTopBar<FlowActionContext, FlowAction>(
				{
					defaultActions,
					selectionActions,
					contextualActions
				},
				hasSettingsSelection,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			selectionActions,
			contextualActions,
			hasSettingsSelection,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): FlowActionContext => ({
			canPublish,
			selectedSettings,
			isMobile,
			isReadOnly
		}),
		[
			canPublish,
			selectedSettings,
			isMobile,
			isReadOnly
		]
	);

	const validateHandler = useCallback(
		async (
			newStep?: {
				data: StepConfiguration;
				index: number;
				clientId: Guid;
			}
		): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const currentStepResults: Map<Guid, IntegrationFlowRequestResultModel> = stepResults;
			let resolvedInputData: InputDataFormData | null = null;
			let stepsToValidate: (readonly ObjectWithClientId<StepConfiguration>[]) | undefined;

			try {
				if (requiresInputData(formData.capabilities)) {
					const inputData: TryResult<InputDataFormData> = await getInputData();

					if (!inputData.wasSuccessful) {
						setIsBusy(false);
						return;
					}

					resolvedInputData = inputData.result;
				}

				stepsToValidate = newStep
					? [
						...formData
							.steps
							.slice(
								0,
								newStep.index
							),
						setExistingClientId(
							newStep.data,
							newStep.clientId
						)
					]
					: formData.steps;

				const steps: readonly StepConfigurationSaveModel[] = stepsToValidate
					.filter(
						(x) => x.kind !== StepKind.Mapping
							|| (x.kind === StepKind.Mapping && isNotNil(x.mapping))
					)
					.map(convertToStepSaveModel);

				const model: IntegrationFlowConfigurationValidateModel = {
					kind: formData.kind,
					capabilities: formData.capabilities,
					steps,
					settings: formData.settings,
					inputData: resolveInputData(formData.capabilities, resolvedInputData)
				};

				clearAllServerErrorsHandler();
				setStepResults(new Map<Guid, IntegrationFlowRequestResultModel>());
				setValidateResponse(null);

				const result: IntegrationFlowConfigurationValidateResponseModel = await validate(
					appId,
					model
				);

				setValidateResponse(result);

				const {
					requestResults,
					validationErrors
				} = result;

				const requestResultsMap = new Map<string, IntegrationFlowRequestResultModel>();

				for (const requestResult of requestResults) {
					requestResultsMap.set(
						requestResult.name,
						requestResult
					);
				}

				const newMap: Map<Guid, IntegrationFlowRequestResultModel> = new Map<Guid, IntegrationFlowRequestResultModel>();

				for (const step of stepsToValidate) {
					switch (step.kind) {
						case StepKind.Mapping:
							continue;
						case StepKind.Request:
						case StepKind.PerItemRequest:
							const result: IntegrationFlowRequestResultModel | undefined = requestResultsMap
								.get(step.name);

							if (result) {
								newMap.set(
									getClientId(step),
									result
								);
							}
							break;
						default:
							never(step);
					}
				}

				setStepResults(newMap);

				if (validationErrors.length > 0) {
					formHandler
						.validation
						.setServerErrors(
							convertServerErrors(
								validationErrors
							)
						);

					setServerErrors(validationErrors);

					if (isNil(newStep)){
						alertManager.showError(localize("_ErrorWhileValidatingTheSteps"));
					}
				} else if (doesResultMapperHasMissingData(formData.steps, result, new Set<Guid>())) {
					alertManager.showWarning(localize("_ForSomeElementsInTheResultMapperNoResultsCouldBeRetrieved"));
				} else if (isNil(newStep)){
					alertManager.showSuccess(localize("_SuccessfullyValidatedTheSteps"));
				}

				setIsBusy(false);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				const result: TryResult<readonly ClientValidationErrorModel[]> = tryGetServerErrors(e);
				let isHandled: boolean = false;

				if (result.wasSuccessful) {

					const {
						result: errors
					} = result;

					if (stepsToValidate) {
						let firstStepIndexWithError: number | null = null;

						for (const error of errors) {
							const result: TryResult<number> = tryExtractStepIndexFromPropertyPath(error.propertyName);

							if (result.wasSuccessful
								&& (firstStepIndexWithError === null || result.result < firstStepIndexWithError)
							) {
								firstStepIndexWithError = result.result;
							}
						}

						if (firstStepIndexWithError !== null) {
							const newStepResults = new Map<Guid, IntegrationFlowRequestResultModel>();

							for (let i = 0; i < stepsToValidate.length && i < firstStepIndexWithError; i++) {
								const stepId: Guid = getClientId(stepsToValidate[i]);
								const currentResult: IntegrationFlowRequestResultModel | undefined = currentStepResults.get(stepId);
								if (currentResult) {
									newStepResults.set(stepId, currentResult);
								}
							}

							setStepResults(newStepResults);
						}
					}

					formHandler
						.validation
						.setServerErrors(convertServerErrors(errors));

					if (hasInputDataError(formData, errors)
						|| hasStepErrors(errors)
					) {
						alertManager.showError(localize("_ErrorWhileValidatingTheSteps"));
					}

					setServerErrors(errors);
					isHandled = true;
				} else {
					const authenticationRequiredResult: TryResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel> = tryParseErrorResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel>(e);

					if (authenticationRequiredResult.wasSuccessful) {
						isHandled = true;

						const {
							authentications: missingAuthentications
						} = authenticationRequiredResult.result;

						const result: TryResult<void> = await openIntegrationFlowAuthenticationRequiredDialog({
							integrationName: !formData.name
								? undefined
								: formData.name,
							missingAuthentications
						});

						if (result.wasSuccessful) {
							validateHandler(newStep);
							return;
						}
					}
				}

				if (!isHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			isBusy,
			alertManager,
			appId,
			clearAllServerErrorsHandler,
			formData,
			formHandler.validation,
			getInputData,
			openIntegrationFlowAuthenticationRequiredDialog,
			stepResults
		]
	);

	const addAuthenticationHandler = useCallback(
		(authentication: IntegrationFlowAuthenticationOptionModel): void => {
			setAuthentications(
				authentications => ([
					...authentications,
					authentication
				])
			);
		},
		[]
	);

	const openStepHandler = useCallback(
		(clientId: Guid): void => {
			const stepsAttachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]> = formHandler
				.for("steps");

			const index: number = stepsAttachable
				.value
				.findIndex((x) => getClientId(x) === clientId);

			if (index === -1) {
				return;
			}

			const attachable: IValueInputAttachable<ObjectWithClientId<StepConfiguration>> = ensureNotNil(
				stepsAttachable.forIndex(index)
			);

			const step: ObjectWithClientId<StepConfiguration> = attachable.value;

			switch(step.kind) {
				case StepKind.Request:
				case StepKind.PerItemRequest:
					setRequestStepDialogState({
						kind: "edit",
						step
					});
					break;
				case StepKind.Mapping:
					const {
						mapping
					} = step;

					const {
						kind
					} = formHandler.value;

					setResultMapperDialogState({
						initialData: mapping ?? getResultMapperInitialData(kind),
						clientId,
						index
					});
					break;
				default:
					never(step);
			}
		},
		[formHandler]
	);

	const createStepHandler = useCallback(
		async (): Promise<void> => {
			const requestStepKindResult: TryResult<RequestStepKind> = await openStepKindSelectionDialog({});

			if (!requestStepKindResult.wasSuccessful) {
				return;
			}

			const stepsAttachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]> = formHandler
				.for("steps");

			const stepsCount: number = stepsAttachable
				.value
				.length;

			const mappingStepIndex: number = stepsAttachable
				.value
				.findIndex(x => x.kind === StepKind.Mapping);

			const indexToInsert: number = mappingStepIndex !== -1
				? Math.max(
					0,
					mappingStepIndex
				)
				: stepsCount;

			const clientId: Guid = newGuid();

			setRequestStepDialogState({
				kind: "new",
				clientId,
				index: indexToInsert,
				requestStepKind: requestStepKindResult.result
			});
		},
		[
			formHandler,
			openStepKindSelectionDialog
		]
	);

	const editInputDataHandler = useCallback(
		async (): Promise<void> => {
			if (!requiresInputData(formData.capabilities)) {
				throw new Error(`The integration capabilities ${formData.capabilities.join(", ")} do not need input data`);
			}

			const inputDataResult: TryResult<InputDataFormData> = await openConfigureInputDataDialog({
				initialData: inputDataRef.current
					?? createEmptyInputData(formData.capabilities)
			});

			if (!inputDataResult.wasSuccessful) {
				return;
			}

			inputDataRef.current = inputDataResult.result;
			notifyAboutNewInputData();
		},
		[
			formData.capabilities,
			notifyAboutNewInputData,
			openConfigureInputDataDialog
		]
	);

	const stepDeletedHandler = useCallback(
		(): void => {
			setServerErrors([]);
		},
		[]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();

			window.addEventListener(
				"beforeunload",
				(event: BeforeUnloadEvent) => {
					if (isDataChanged) {
						beforeUnloadListener(event);
					}
				},
				{ signal: cancellation.signal }
			);

			return () => cancellation.abort();
		},
		[
			isDataChanged,
			globalCancellation
		]
	);

	useEffect(
		(): (() => void) => {
			setIsBusy(true);
			setStepResults(new Map<Guid, IntegrationFlowRequestResultModel>());
			setIsReadOnly(false);
			setServerErrors([]);
			inputDataRef.current = null;
			notifyAboutNewInputData();

			setUsersAndUserGroupsData({
				users: EMPTY_ARRAY,
				userGroups: EMPTY_ARRAY
			});

			setInitialFormData(defaultFormData);
			setFormData(defaultFormData);

			const load = async (
				id: CompositeId,
				signal: AbortSignal
			): Promise<void> => {
				try {
					const isEdit = id.kind === "edit";
					let pageModel: IntegrationFlowConfigurationPageViewModel;

					if (isEdit) {
						pageModel = await getEditViewModel(
							id.integrationId,
							id.versionId,
							signal
						)
							.catch(handlePageDataLoadingError);
					} else {
						pageModel = await getNewViewModel(
							appId,
							id.flowKind,
							signal
						)
							.catch(handlePageDataLoadingError);
					}

					const {
						isAllowedToConfigureSearchSettings,
						model
					} = pageModel;

					setIsReadOnly(model.status !== IntegrationFlowStatus.Draft);
					const formData: FormData = convertToFormData(
						model,
						!isEdit,
						isEdit
							? EMPTY_ARRAY
							: id.capabilities
					);

					setHasLiveVersion(model.hasLiveVersion);
					setIsAllowedToConfigureSearchSettings(isAllowedToConfigureSearchSettings);
					setFormData(formData);
					setInitialFormData(formData);
					setAuthentications(model.authentications);
					setSharedSettings(model.sharedSettings);
					setUsersAndUserGroupsData(model.usersAndUserGroupsData);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(compositeId, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			appId,
			compositeId,
			notifyAboutNewInputData
		]
	);

	const saveResultMapperHandler = useCallback(
		(
			data: MappingStepConfigurationSaveModel,
			clientId: Guid,
			index: number
		): void => {
			const stepsAttachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]> = formHandler
				.for("steps");

			const step: ObjectWithClientId<StepConfiguration> = setExistingClientId(
				convertMappingStepToStepConfiguration(data),
				clientId
			);

			const attachable: IValueInputAttachable<ObjectWithClientId<StepConfiguration>> = ensureNotNil(
				stepsAttachable.forIndex(index)
			);

			attachable
				.reducer
				.setValue(step);

			attachable
				.validation
				.triggerInputFinished();
		},
		[formHandler]
	);

	const saveRequestStepHandler = useCallback(
		(
			newStep: RequestStepConfiguration | PerItemRequestStepConfiguration,
			clientId: Guid,
			index: number,
			isEdit: boolean
		): void => {
			const stepsAttachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]> = formHandler
				.for("steps");

			const step: ObjectWithClientId<StepConfiguration> = setExistingClientId(
				newStep,
				clientId
			);

			if (isEdit) {
				const attachable: IValueInputAttachable<ObjectWithClientId<StepConfiguration>> = ensureNotNil(
					stepsAttachable.forIndex(index)
				);

				attachable
					.reducer
					.setValue(step);

				attachable
					.validation
					.triggerInputFinished();
			} else {
				stepsAttachable
					.reducer
					.insertAt(
						index,
						step
					);

				stepsAttachable
					.validation
					.triggerInputFinished();
			}

			stepsAttachable.validation
				.triggerInputFinished();
		},
		[formHandler]
	);

	const renderRequestStepDialog = (): ReactElement | null => {
		if (!requestStepDialogState) {
			return null;
		}

		let initialData: StepDialogInitialData;
		let clientId: Guid;
		let index: number;

		switch (requestStepDialogState.kind) {
			case "edit":
				initialData = {
					kind: "edit",
					data: requestStepDialogState.step
				};

				clientId = getClientId(requestStepDialogState.step);
				const stepsAttachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]> = formHandler
					.for("steps");

				const stepIndex: number = stepsAttachable
					.value
					.findIndex((x) => getClientId(x) === clientId);

				if (stepIndex === -1) {
					return null;
				}

				index = stepIndex;
				break;
			case "new":
				initialData = {
					kind: "new",
					requestStepKind: requestStepDialogState.requestStepKind
				};

				clientId = requestStepDialogState.clientId;
				index = requestStepDialogState.index;
				break;
			default:
				never(requestStepDialogState);
		}

		return (
			<StepDialog
				authentications={authentications}
				serverErrors={serverErrors}
				stepResults={stepResults}
				stepNames={
					requestSteps.map(
						x => x.name
					)
				}
				onAddAuthentication={addAuthenticationHandler}
				onValidate={
					(newStep) => validateHandler({
						data: newStep,
						clientId,
						index
					})
				}
				stepClientId={clientId}
				stepIndex={index}
				initialData={initialData}
				isReadOnly={isReadOnly}
				onClose={() => setRequestStepDialogState(null)}
				onDone={
					step => saveRequestStepHandler(
						step,
						clientId,
						index,
						initialData.kind === "edit"
					)
				}
			/>
		);
	};

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				title={localize(
					compositeId.kind === "edit"
						? "_EditFlow"
						: "_CreateNewFlow"
				)}
				actionContext={actionContext}
				actions={topBarActions}
				hasSelection={hasSettingsSelection}
				onBack={backHandler}
				backButtonText={localize("_Flows")}
			>
				<div className={styles.root}>
					{isReadOnly && (
						<AlertBar
							isInline
							kind="info"
							isDismissable={false}
						>
							<FormattedText
								text={localize("_ReadOnlyThisIsLiveAndCannotBeEdited")}
								thingsToWrap={[
									{
										blockId: "ReadOnly",
										wrap: (x) => <b>{x}</b>
									}
								]}
							/>
						</AlertBar>
					)}

					<Form onSubmit={saveHandler} height="fill">
						<Tabs<TabId>
							height="fill"
							onActiveTabIdChange={setActiveTabId}
						>
							<Tabs.Tab
								{...formHandler.forStep(generalValidationStep).attachToTab()}
								id="general"
								title={localize("_General")}
							>
								<General
									isReadOnly={isReadOnly}
									canEditCapabilities={compositeId.kind === "new"}
									attachable={formHandler}
								/>
							</Tabs.Tab>

							<Tabs.Tab
								{...formHandler.forStep(stepsValidationStep).attachToTab()}
								id="steps"
								title={localize("_Steps")}
							>
								<Steps
									attachable={formHandler.for("steps")}
									stepResults={stepResults}
									isReadOnly={isReadOnly}
									onOpenStep={openStepHandler}
									onAddStep={createStepHandler}
									onEditInputData={hasInputData
										? editInputDataHandler
										: undefined
									}
									onValidate={validateHandler}
									stepWithErrorIds={stepWithErrorIds}
									onStepDeleted={stepDeletedHandler}
									validateResponse={validateResponse}
								/>
							</Tabs.Tab>
							<Tabs.Tab
								{...formHandler.forStep(settingsValidationStep).attachToTab()}
								id="settings"
								title={localize("_Settings")}
							>
								<SettingsTab
									attachable={formHandler.for("settings")}
									sharedSettings={sharedSettings}
									indicesWithServerErrors={settingIndicesWithServerErrors}
									selectedItems={selectedSettings}
									onSelectedItemsChange={setSelectedSettings}
									onOpenSettingsDialog={openSettingsDialogHandler}
									isReadOnly={isReadOnly}
								/>
							</Tabs.Tab>
							<Tabs.Tab
								{...formHandler.forStep(whoHasAccessValidationStep).attachToTab()}
								id="who-has-access"
								title={localize("_WhoHasAccess")}
							>
								{formHandler.for("permissions").whenType(
									(x): x is SetOptionalModel<IntegrationFlowPermissionsSaveModel> => x.isSet,
										attachable => (
											<WhoHasAccessForNewIntegration
												attachable={attachable.for("value")}
												isReadOnly={isReadOnly}
												usersAndUserGroupsData={usersAndUserGroupsData}
											/>
										),
									() => <WhoHasAccessForExistingIntegration />
								)}
							</Tabs.Tab>
						</Tabs>
					</Form>
				</div>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{stepKindSelectionDialog}
			{renderRequestStepDialog()}
			{resultMapperDialogState && (
				<ResultMapperDialog
					initialData={resultMapperDialogState.initialData}
					generalSample={validateResponse && isGeneralValidationResults(validateResponse)
						? validateResponse.generalSample
						: null
					}
					serverErrors={serverErrors}
					stepResults={stepResults}
					stepWithErrorIds={stepWithErrorIds}
					onValidate={
						(mapping) => validateHandler({
							data: {
								kind: StepKind.Mapping,
								mapping
							},
							clientId: resultMapperDialogState.clientId,
							index: resultMapperDialogState.index
						})
					}
					steps={requestSteps}
					stepIndex={resultMapperDialogState.index}
					isReadOnly={isReadOnly}
					onClose={() => setResultMapperDialogState(null)}
					onDone={(data) => saveResultMapperHandler(
						data,
						resultMapperDialogState.clientId,
						resultMapperDialogState.index
					)}
				/>
			)}
			{configureInputDataDialog}
			{unsavedChangesDialog}
			{settingsDialog}
			{confirmationDialog}
			{integrationFlowAuthenticationRequiredDialog}
		</>
	);
};
EditIntegrationFlowPage.displayName = "EditIntegrationFlowPage";

type TabId =
	| "general"
	| "steps"
	| "settings"
	| "who-has-access";

type ResultMapperDialogState = {
	initialData: MappingStepConfigurationSaveModel;
	clientId: Guid;
	index: number;
};

type RequestStepDialogState = {
	kind: "edit";
	step: ObjectWithClientId<RequestStepConfiguration | PerItemRequestStepConfiguration>;
} | {
	kind: "new";
	clientId: Guid;
	index: number;
	requestStepKind: RequestStepKind;
};

type GeneralProps = {
	isReadOnly: boolean;
	canEditCapabilities: boolean;
	attachable: IValueInputAttachable<FormData>;
};

const General = (props: GeneralProps): ReactElement => {
	const {
		isReadOnly,
		canEditCapabilities,
		attachable
	} = props;

	const {
		kind,
		capabilities,
		color
	} = attachable.reducer
		.value;

	const actualColor: AppColorViewModel | null = convertColorToViewModel(
		color,
		{ kind: AppColorKind.Primary }
	);

	const supportsSearch: boolean = integrationFlowKindToCapabilities[kind]
		.supported
		.includes(IntegrationFlowCapabilityKind.Search);

	const isSearchRequired: boolean = integrationFlowKindToCapabilities[kind]
		.required
		.includes(IntegrationFlowCapabilityKind.Search);

	const hasSearch: boolean = capabilities.includes(IntegrationFlowCapabilityKind.Search);

	const searchToggledHandler = useCallback(
		(isEnabled: boolean): void => {
			const capabilitiesAttachable: IValueInputAttachable<readonly IntegrationFlowCapabilityKind[]> = attachable
				.for("capabilities");

			const withoutSearch: readonly IntegrationFlowCapabilityKind[] = capabilitiesAttachable
				.value
				.filter(x => x !== IntegrationFlowCapabilityKind.Search);

			capabilitiesAttachable
				.reducer
				.setValue(
					isEnabled
						? [
							...withoutSearch,
							IntegrationFlowCapabilityKind.Search
						]
						: withoutSearch
				);

			capabilitiesAttachable
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	return (
		<div
			className={classNames(styles.tabContent, styles.tabContent_widthContrained)}
		>
			<FormItem label={localize("_Name")} isRequired>
				{isReadOnly
					? (
						<TextInputReadOnly {...attachable.for("name").attachToTextInput()} />
					)
					: (
						<>
							<TextInput
								{...attachable.for("name").attachToTextInput()}
								placeholder={localize("_EnterIntegrationName")}
							/>
							<ValidationError {...attachable.for("name").attachToValidationError()} />
						</>
					)
				}
			</FormItem>

			<FormItem label={localize("_ItemType")}>
				{isReadOnly
					? (
						<SelectReadOnly
							{...attachable.for("kind").attachToSelect()}
							options={kindOptions}
						/>
					)
					: (
						<>
							<Select
								{...attachable.for("kind").attachToSelect()}
								options={kindOptions}
								menuTitle={localize("_ItemType")}
								isDisabled
							/>

							<ValidationError {...attachable.for("kind").attachToValidationError()} />
						</>
					)
				}
			</FormItem>

			<FormItem
				label={localize("_Icon")}
			>
				<AppIconSelector
					{...attachToAppIconSelector(
						attachable.for("image"),
						isReadOnly
					)}
					color={actualColor}
				/>
				<ValidationError {...attachable.under("image").attachToValidationError()} />
			</FormItem>

			<AppColorSelectFormItem
				attachable={attachable.for("color")}
				isReadOnly={isReadOnly}
			/>

			<FormItem label={localize("_Tags")}>
				<IntegrationFlowTagSelect
					attachable={attachable.for("tags")}
					isReadOnly={isReadOnly}
					menuTitle={localize("_Tags")}
				/>
				<ValidationError {...attachable.for("tags").attachToValidationError()} />
			</FormItem>

			{supportsSearch && !isSearchRequired && (
				<FormItem label={localize("_Search")}>
					<Checkbox
						value={hasSearch}
						onChange={searchToggledHandler}
						text={localize("_MakeTheResultsSearchable")}
						trackingId="has-search-capability"
						isDisabled={isReadOnly || !canEditCapabilities}
					/>

					<FormItem.Description>
						{localize("_UseThisForGlobalSearchAndSearchableLiveTiles")}
					</FormItem.Description>
				</FormItem>
			)}
		</div>
	);
};
General.displayName = "General";

type WhoHasAccessForNewIntegrationProps = {
	attachable: IValueInputAttachable<IntegrationFlowPermissionsSaveModel>;
	isReadOnly: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

const WhoHasAccessForNewIntegration = (props: WhoHasAccessForNewIntegrationProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		usersAndUserGroupsData
	} = props;

	return (
		<div className={styles.tabContent}>
			<FormItem>
				{isReadOnly
					? (
						<CheckboxReadOnly
							{...attachable.for("everyoneHasAccess")
								.attachToCheckbox()
							}
							text={localize("_EveryoneHasAccess")}
						/>
					)
					: (
						<>
							<Checkbox
								{...attachable.for("everyoneHasAccess")
									.attachToCheckbox()
								}
								text={localize("_EveryoneHasAccess")}
							/>

							<ValidationError {...attachable.for("everyoneHasAccess").attachToValidationError()} />
						</>
					)
				}
			</FormItem>

			{!attachable.value.everyoneHasAccess && (
				<FormItem height="stretch">
					<UserAndUserGroupCollection
						activeTab={ActiveTab.UserGroup}
						{...attachToUserAndUserGroupCollection(
							attachable.for("whoHasAccess"),
							isReadOnly
								? { kind: UserAndUserGroupKind.Read }
								: {
									kind: UserAndUserGroupKind.Edit,
									dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
								}
						)}
						allUsersAndUserGroupsData={usersAndUserGroupsData}
						requiredUserLicense={integrationFlowRequiredUserLicense}
					/>

					{!isReadOnly && (
						<ValidationError {...attachable.for("whoHasAccess").attachToValidationError()} />
					)}
				</FormItem>
			)}
		</div>
	);
};
WhoHasAccessForNewIntegration.displayName = "WhoHasAccessForNewIntegration";

const WhoHasAccessForExistingIntegration = (): ReactElement => {
	return (
		<div className={styles.tabContent}>
			<AlertBar
				isInline
				kind="info"
				isDismissable={false}
			>
				{localize("_ToManageIntegrationPermissionsExplanation")}
			</AlertBar>
		</div>
	);
};
WhoHasAccessForExistingIntegration.displayName = "WhoHasAccessForExistingIntegration";

type StepsProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<StepConfiguration>[]>;
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>;
	isReadOnly: boolean;
	stepWithErrorIds: ReadonlySet<Guid>;
	validateResponse: IntegrationFlowConfigurationValidateResponseModel | null;
	onOpenStep: (clientId: Guid) => void;
	onAddStep: () => void;
	onValidate: () => void;
	onStepDeleted(): void;
	onEditInputData?: () => void;
};

const Steps = (props: StepsProps): ReactElement => {
	const {
		attachable,
		stepResults,
		isReadOnly,
		stepWithErrorIds,
		validateResponse,
		onOpenStep,
		onAddStep,
		onEditInputData,
		onValidate,
		onStepDeleted
	} = props;

	const hasAnyRequestSteps: boolean = attachable
		.value
		.some(
			x => x.kind === StepKind.Request
		);

	const hasReachedMaxSteps: boolean = attachable
		.value
		.length >= MAX_STEP_COUNT;

	return (
		<div className={styles.tabContent}>
			<FormItem>
				{!isReadOnly && (
					<Toolbar>
						<Toolbar.Button
							kind="secondary"
							text={localize("_NewStep")}
							icon="plus"
							onClick={onAddStep}
							trackingId="new-step"
							isDisabled={hasReachedMaxSteps}
						/>

						{onEditInputData && (
							<Toolbar.Button
								kind="secondary"
								text={localize("_EditInputData")}
								icon="pen"
								onClick={onEditInputData}
								trackingId="edit-input-data"
							/>
						)}

						{hasAnyRequestSteps && (
							<Toolbar.Button
								kind="secondary"
								text={localize("_Validate")}
								icon="check"
								onClick={onValidate}
								trackingId="validate"
							/>
						)}
					</Toolbar>
				)}

				<StepList
					attachable={attachable}
					stepResults={stepResults}
					isReadOnly={isReadOnly}
					onOpenStep={onOpenStep}
					stepWithErrorIds={stepWithErrorIds}
					onStepDeleted={onStepDeleted}
					validateResponse={validateResponse}
				/>
			</FormItem>
		</div>
	);
};
Steps.displayName = "Steps";

type FormData = OverrideProperties<
	Omit<IntegrationFlowConfigurationSaveModel, "inputData">,
	{
		image: AppIconModel | null;
		steps: readonly ObjectWithClientId<StepConfiguration>[];
	}
>;

const defaultFormData: FormData = {
	kind: IntegrationFlowKind.General,
	capabilities: EMPTY_ARRAY,
	name: "",
	color: { kind: AppColorKind.Inherit },
	image: null,
	permissions: {
		isSet: false
	},
	steps: [
		setClientId({
			kind: StepKind.Mapping,
			mapping: null
		})
	],
	settings: EMPTY_ARRAY,
	tags: EMPTY_ARRAY
};

const kindOptions: readonly SelectItem<IntegrationFlowKind>[] = [
	{
		value: IntegrationFlowKind.General,
		text: localize(integrationFlowKindToLocalizationKey[IntegrationFlowKind.General])
	}
];

const MAX_STEP_COUNT = 20;
const MIN_STEP_COUNT = 2;
export const MAX_SETTINGS_COUNT = 20;

const validationConfiguration: IValidationConfiguration<FormData> = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forCollection("steps")
	.add(
		hasMaximumEntries(MAX_STEP_COUNT)
	);

validationConfiguration.forCollection("settings")
	.add(
		hasMaximumEntries(MAX_SETTINGS_COUNT)
	);

const generalValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"name",
		"kind",
		"image",
		"color"
	);

const stepsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"steps"
	);

const settingsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"settings"
	);

const whoHasAccessValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"permissions"
	);

function convertToFormData(
	model: IntegrationFlowConfigurationViewModel,
	isCreatingNew: boolean,
	selectedCapabilities: readonly IntegrationFlowCapabilityKind[]
): FormData {
	const {
		kind,
		name,
		whoHasAccess,
		everyoneHasAccess,
		color,
		tags
	} = model;

	const capabilities: readonly IntegrationFlowCapabilityKind[] = isCreatingNew
		? union(model.capabilities, selectedCapabilities)
		: model.capabilities;

	const shouldAddMapping: boolean = model
		.steps
		.findIndex(x => x.kind === StepKind.Mapping) === -1;

	const steps: ObjectWithClientId<StepConfiguration>[] = model.steps
		.map(
			(x) => setClientId(
				convertToStepFormData(model.kind, x)
			)
		);

	const settings: readonly IntegrationFlowSettingSaveModel[] = model.settings
		.map(convertToSettingSaveModel);

	return {
		kind,
		capabilities,
		name,
		color,
		settings,
		permissions: isCreatingNew
			? {
				isSet: true,
				value: {
					everyoneHasAccess,
					whoHasAccess
				}
			}
			: {
				isSet: false
			},
		image: {
			...model.image,
			url: model.imageDisplayInfo
				.imageUrl,
			renderType: model.imageDisplayInfo
				.renderType
		},
		steps: shouldAddMapping
			? [
				...steps,
				setClientId({
					kind: StepKind.Mapping,
					mapping: null
				})
			]
			: steps,
		tags
	};
}

// We need this custom server error processing
// because for step dialogs the validation errors
// must be passed with different path redirects
function tryGetServerErrors(
	error: unknown,
): TryResult<readonly ClientValidationErrorModel[]> {
	if (!(error instanceof HttpRequestError && error.statusCode === 400)) {
		return { wasSuccessful: false };
	}

	try {
		const errors: readonly ClientValidationErrorModel[] = error
			.readJson<ClientValidationErrorModel[]>();

		return {
			wasSuccessful: true,
			result: errors
		};
	} catch (e) {
		console.error(e);
		return { wasSuccessful: false };
	}
}

const defaultResultMapperStyledFieldSaveModel: ResultMapperStyledFieldSaveModel = {
	expression: "",
	colors: null
};

function getResultMapperInitialData(
	kind: IntegrationFlowKind
): MappingStepConfigurationSaveModel {
	switch(kind) {
		case IntegrationFlowKind.General:
			return {
				resultCollectionExpression: "",
				general: {
					linkExpression: "",
					title: defaultResultMapperStyledFieldSaveModel,
					date: defaultResultMapperStyledFieldSaveModel,
					subtitle1: defaultResultMapperStyledFieldSaveModel,
					subtitle2: defaultResultMapperStyledFieldSaveModel,
					subtitle3: defaultResultMapperStyledFieldSaveModel,
					description: defaultResultMapperStyledFieldSaveModel
				}
			};
		default:
			never(kind);
	}
}

function convertToStepFormData(
	kind: IntegrationFlowKind,
	model: StepConfigurationViewModel
): StepConfiguration {
	switch (model.kind) {
		case StepKind.Request:
			return {
				kind: StepKind.Request,
				name: model.name,
				authenticationId: model.authenticationId,
				method: model.method,
				url: model.url,
				queryParameters: model.queryParameters
					.map((x) => setClientId(x)),
				headers: model.headers
					.map((x) => setClientId(x)),
				body: model.body
			};
		case StepKind.PerItemRequest:
			return {
				kind: StepKind.PerItemRequest,
				name: model.name,
				authenticationId: model.authenticationId,
				method: model.method,
				url: model.url,
				queryParameters: model.queryParameters
					.map((x) => setClientId(x)),
				headers: model.headers
					.map((x) => setClientId(x)),
				body: model.body,
				maxItems: model.maxItems,
				stepCollection: model.stepCollection
			};
		case StepKind.Mapping:
			return {
				kind: StepKind.Mapping,
				mapping: convertToMappingFormData(kind, model)
			};
		default:
			never(model);
	}
}

function convertToMappingFormData(
	kind: IntegrationFlowKind,
	mapping: MappingStepConfigurationViewModel
): MappingStepConfigurationSaveModel {
	switch (kind) {
		case IntegrationFlowKind.General:
			if (!isGeneralMappingStepConfigurationViewModel(mapping)) {
				throw new Error("Invalid mapping step configuration for General integration flow");
			}

			return {
				resultCollectionExpression: mapping.resultCollectionExpression,
				general: {
					linkExpression: mapping.linkExpression,
					title: convertResultMapperStyledFieldToSaveModel(mapping.title)
						?? {
							expression: "",
							colors: null
						},
					date: convertResultMapperStyledFieldToSaveModel(mapping.date),
					description: convertResultMapperStyledFieldToSaveModel(mapping.description),
					subtitle1: convertResultMapperStyledFieldToSaveModel(mapping.subtitle1),
					subtitle2: convertResultMapperStyledFieldToSaveModel(mapping.subtitle2),
					subtitle3: convertResultMapperStyledFieldToSaveModel(mapping.subtitle3)
				}
			};
		default:
			never(kind);
	}
}

function convertResultMapperStyledFieldToSaveModel(viewModel: ResultMapperStyledFieldViewModel): ResultMapperStyledFieldSaveModel | null {
	const {
		expression,
		colors
	} = viewModel;

	if (!expression && !colors) {
		return null;
	}

	return {
		expression,
		colors
	};
}

function isGeneralMappingStepConfigurationViewModel(
	model: MappingStepConfigurationViewModel
): model is GeneralMappingStepConfigurationViewModel {
	return isNotNil((model as GeneralMappingStepConfigurationViewModel).title)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).linkExpression)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).date)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).subtitle1)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).subtitle2)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).subtitle3)
		&& isNotNil((model as GeneralMappingStepConfigurationViewModel).description);
}

type FlowActionContext = {
	canPublish: boolean;
	selectedSettings: readonly SettingItem[];
	isMobile: boolean;
	isReadOnly: boolean;
};

type FlowAction = ToolbarAction<FlowActionContext>;

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly FlowAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => deselectAllTitle(c.selectedSettings),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedSettings.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedSettings.length > 0
		}
	];
}

type DefaultActionHandlers = {
	save: () => void;
	publish: () => void;
	cancel: () => void;
};

function listDefaultActions(handlers: DefaultActionHandlers): readonly FlowAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true,
			icon: "floppy-disk"
		},
		{
			kind: ActionKind.Regular,
			name: "publish",
			title: localize("_Publish"),
			canExecute: () => true,
			isDisabled: (c) => !c.canPublish,
			execute: () => handlers.publish(),
			icon: "arrow-up-to-line"
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.cancel(),
			icon: "xmark"
		}
	];
}

type ContextualActionHandlers = {
	onDeleteSelected: () => void;
	onSettingEdit: (setting: IntegrationFlowSettingSaveModel) => void;
};

function listContextualActions(handlers: ContextualActionHandlers): readonly FlowAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit-setting",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (c) => !c.isReadOnly
				&& c.selectedSettings.length === 1,
			execute: (c) => handlers.onSettingEdit(c.selectedSettings[0].setting)
		},
		{
			kind: ActionKind.Regular,
			name: "delete-settings",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => !c.isReadOnly
				&& c.selectedSettings.length > 0,
			execute: () => handlers.onDeleteSelected()
		}
	];
}

function convertMappingStepToStepConfiguration(
	step: MappingStepConfigurationSaveModel
): StepConfiguration {
	return {
		kind: StepKind.Mapping,
		mapping: step
	};
}

function prepareForSaving(
	model: FormData,
	inputData: InputDataFormData | null,
	isEdit?: boolean,
): IntegrationFlowConfigurationSaveModel {
	const {
		kind,
		capabilities,
		name,
		color,
		steps,
		settings,
		image,
		permissions,
		tags
	} = model;

	return {
		kind,
		capabilities,
		name,
		color,
		steps: steps
			.filter(
				(x) => x.kind !== StepKind.Mapping
					|| (x.kind === StepKind.Mapping && isNotNil(x.mapping))
			)
			.map(convertToStepSaveModel),
		settings,
		image: ensureNotNil(image),
		permissions: isEdit
			? { isSet: false }
			: permissions,
		inputData: resolveInputData(capabilities, inputData),
		tags
	};
}

export function resolveInputData(
	capabilities: readonly IntegrationFlowCapabilityKind[],
	formData: InputDataFormData | null
): IntegrationFlowInputDataModel {
	return {
		search: isNotNil(formData)
			&& capabilities.includes(IntegrationFlowCapabilityKind.Search)
			? {
				isSet: true,
				value: formData.searchTerm
			}
			: { isSet: false }
	};
}

function convertToSettingSaveModel(
	setting: IntegrationFlowSettingViewModel
): IntegrationFlowSettingSaveModel {
	switch (setting.scopeKind) {
		case IntegrationSettingScopeKind.Shared:
			return {
				scopeKind: IntegrationSettingScopeKind.Shared,
				key: setting.key,
				sharedSettingId: setting.sharedSettingId
			};
		case IntegrationSettingScopeKind.ThisFlow:
			return {
				scopeKind: IntegrationSettingScopeKind.ThisFlow,
				key: setting.key,
				value: setting.value,
				displayName: setting.displayName,
				description: setting.description ?? ""
			};
		default:
			return never(setting);
	}
}

function convertToStepSaveModel (
	step: ObjectWithClientId<StepConfiguration>
): StepConfigurationSaveModel {
	switch(step.kind) {
		case StepKind.Request:
			return {
				kind: StepKind.Request,
				request: {
					name: step.name,
					headers: step.headers,
					authenticationId: step.authenticationId,
					method: step.method,
					queryParameters: step.queryParameters,
					url: step.url,
					body: step.method === RequestMethod.Get
						? null
						: step.body
				}
			};
		case StepKind.PerItemRequest:
			return {
				kind: StepKind.PerItemRequest,
				perItemRequest: {
					name: step.name,
					headers: step.headers,
					authenticationId: step.authenticationId,
					method: step.method,
					queryParameters: step.queryParameters,
					url: step.url,
					body: step.method === RequestMethod.Get
						? null
						: step.body,
					maxItems: ensureNotNil(step.maxItems),
					stepCollection: step.stepCollection
				}
			};
		case StepKind.Mapping:
			return {
				kind: StepKind.Mapping,
				mapping: ensureNotNil(step.mapping)
			};
		default:
			never(step);
	}
}

function hasInputDataError(
	model: FormData,
	errors: readonly ClientValidationErrorModel[]
): boolean {
	if (!requiresInputData(model.capabilities)) {
		return false;
	}

	return errors.some(
		error => error
			.propertyName
			.startsWith("inputData")
	);
}

function hasStepErrors(
	errors: readonly ClientValidationErrorModel[]
): boolean {
	return errors.some(
		error => error
			.propertyName
			.startsWith("Steps")
	);
}

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};

function getStepWithErrorsIds(
	steps: readonly ObjectWithClientId<StepConfiguration>[],
	serverErrors: readonly ClientValidationErrorModel[]
): Set<Guid> {
	const result = new Set<Guid>();

	for (const error of serverErrors) {
		const match: RegExpMatchArray | null = error
			.propertyName
			.match(/Steps\[(\d+)\]/);

		if (!match) {
			continue;
		}

		const errorIndex: number = parseInt(match[1], 10);

		const errorStep: ObjectWithClientId<StepConfiguration> = ensureNotNil(
			steps[errorIndex]
		);

		const clientId: Guid = getClientId(errorStep);

		result.add(clientId);
	}

	return result;
}

function doesResultMapperHasMissingData(
	steps: readonly ObjectWithClientId<StepConfiguration>[],
	validateResponse: IntegrationFlowConfigurationValidateResponseModel,
	stepWithErrorIds: Set<Guid>
): boolean {
	const resultMapperStep: ObjectWithClientId<MappingStepConfiguration> | undefined = steps.find(
		x => x.kind === StepKind.Mapping
	);

	if (!resultMapperStep) {
		return false;
	}

	return getResultMapperStepStatus(
		resultMapperStep,
		validateResponse,
		stepWithErrorIds
	) === "missing-data";
}

function tryExtractStepIndexFromPropertyPath(propertyPath: string): TryResult<number> {
	const matchResult: RegExpMatchArray | null = propertyPath.match(/^Steps\[(\d+)\]/);
	if (!matchResult || matchResult.length !== 2) {
		return { wasSuccessful: false };
	}

	return tryParseInt(matchResult[1]);
}

const SETTING_INDEX_PATTERN = /^Settings\[(\d+)\]/;

function tryExtractSettingsIndexFromPropertyPath(propertyPath: string): TryResult<number> {
	const matchResult: RegExpMatchArray | null = propertyPath.match(SETTING_INDEX_PATTERN);
	if (!matchResult || matchResult.length !== 2) {
		return { wasSuccessful: false };
	}

	return tryParseInt(matchResult[1]);
}

const tryParseCapabilityKind = createTryParseNumericEnum(IntegrationFlowCapabilityKind);

export function tryParseCapabilities(
	values: readonly string[]
): TryResult<readonly IntegrationFlowCapabilityKind[]> {
	const capabilities: IntegrationFlowCapabilityKind[] = [];

	for (const value of values) {
		const kindResult: TryResult<IntegrationFlowCapabilityKind> = tryParseCapabilityKind(value);

		if (!kindResult.wasSuccessful) {
			return { wasSuccessful: false };
		}

		if (!capabilities.includes(kindResult.result)) {
			capabilities.push(kindResult.result);
		}
	}

	return {
		wasSuccessful: true,
		result: capabilities
	};
}

export function getSettingServerErrors(
	serverErrors: readonly ClientValidationErrorModel[],
	index: number
): readonly ClientValidationErrorModel[] {
	const pattern: DotPathWildcardPattern = new DotPathWildcardPattern(`Settings[${index}].**`);

	return serverErrors
		.map((error): ClientValidationErrorModel | undefined => {
			const result: TryResult<string> = pattern.tryReplace(error.propertyName, "{0}");

			return result.wasSuccessful
				? {
					propertyName: result.result,
					errorMessage: error.errorMessage
				}
				: undefined;
		})
		.filter(isNotNil);
}

type CompositeId = {
	kind: "new";
	flowKind: IntegrationFlowKind;
	capabilities: readonly IntegrationFlowCapabilityKind[];
} | {
	kind: "edit";
	integrationId: number;
	versionId: Guid;
};

export type NewIntegrationFlowRouteParams = RouteParametersFromProps<
	EditIntegrationFlowPageProps,
	"appId"
>;

export type NewIntegrationFlowQueryParams = QueryParametersFromProps<
	EditIntegrationFlowPageProps,
	"flowKind" | "capabilities"
>;

export type EditIntegrationFlowRouteParams = RouteParametersFromProps<
	EditIntegrationFlowPageProps,
	"appId"
	| "integrationId"
	| "versionId"
>;

export const EditIntegrationFlowPageForRouter = connectToRouter(
	EditIntegrationFlowPage,
	{
		"appId": {
			isRequired: true,
			tryParse: tryParseInt,
			type: "route"
		},
		"integrationId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "route"
		},
		"versionId": {
			isRequired: false,
			tryParse: tryParseGuid,
			type: "route"
		},
		"flowKind": {
			isRequired: false,
			tryParse: createTryParseNumericEnum(IntegrationFlowKind),
			type: "query"
		},
		"capabilities": {
			isRequired: false,
			doesSupportMultiple: true,
			tryParse: tryParseCapabilities,
			type: "query"
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);
